import axios, { type AxiosError, type AxiosProgressEvent } from 'axios';
import { atom, createStore } from 'jotai'; // or from 'jotai/vanilla'
import {
  type CacheConfig,
  Environment,
  Network,
  RecordSource,
  type RequestParameters,
  Store,
  type UploadableMap,
  type Variables,
} from 'relay-runtime';

// use jotai to store the upload progress
// this allows us to access the progress from anywhere in the app without instantiating a new relay environment
const store = createStore();
export const uploadProgressAtom = atom<AxiosProgressEvent | null>(null);

export const graphqlEndpoint = `${window.location.origin.replace('localhost:3000', 'localhost:1306')}/graphql`;

type GraphQlError = {
  debugMessage?: string;
  message: string;
  // biome-ignore lint: @todo - biome refactor
  locations: any[];
  extensions: { category: string };
  path: string[];
};

type Response = {
  // biome-ignore lint: @todo - biome refactor
  data: any;
  errors?: GraphQlError[];
};

// Relay passes a "params" object with the query name and text. So we define a helper function
// to call our fetchGraphQL utility with params.text.
export async function fetchRelay(
  operation: RequestParameters,
  variables: Variables,
  cacheConfig?: CacheConfig,
  uploadables?: UploadableMap | null
) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // add wp nonce if it exists
    ...(window?.znRuntime?.restToken && window.location.host !== 'localhost:3000'
      ? { 'X-WP-Nonce': window.znRuntime.restToken }
      : {}),
  };
  let body: string | FormData = JSON.stringify({
    query: operation.text,
    variables,
  });

  if (!!uploadables && typeof uploadables === 'object' && Object.keys(uploadables).length > 0) {
    // biome-ignore lint: @todo - biome refactor
    const map: any = {};
    const formData = new FormData();

    formData.append('operations', body);

    Object.keys(uploadables).forEach((key, idx) => {
      if (Object.prototype.hasOwnProperty.call(uploadables, key))
        // variables.files.0; The "files" aspect is super important. needs to match the variable name for files
        map[idx] = [`variables.${key}`];
    });
    formData.append('map', JSON.stringify(map));

    Object.keys(uploadables).forEach((key, idx) => {
      if (Object.prototype.hasOwnProperty.call(uploadables, key))
        formData.append(
          idx.toString(),
          uploadables[key],
          (uploadables[key] as unknown as Record<string, string>)?.name
        );
    });

    headers['Content-Type'] = 'multipart/form-data';
    body = formData;
  }

  try {
    const response = await axios.post(graphqlEndpoint, body, {
      headers,
      onUploadProgress: (progressEvent) => {
        store.set(uploadProgressAtom, progressEvent);
      },
    });

    return { ...response.data } as Response;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<Response>;
      if (error.response?.status === 401) {
        window.location.href = `${window.location.origin}/auth/login`;
      }
      return { errors: error.response?.data?.errors } as Response;
    }
    return { errors: [{ message: (e as Error)?.message ?? 'Unknown Error Occured' }] } as Response;
  }
}

const relayEnv = new Environment({
  configName: 'relay',
  network: Network.create(fetchRelay),
  store: new Store(new RecordSource()),
});

export const RelayEnvironment = relayEnv;

export { store as RelayEnvironmentStore };
